import React, { useContext, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';

/**
 * A guard component that checks if the user needs to change their password
 * and redirects them to the change password page if needed.
 */
const PasswordChangeGuard = ({ children }) => {
  const { user, loading } = useContext(AuthContext);
  const location = useLocation();
  
  // Log the current state for debugging
  useEffect(() => {
    console.debug('[PASSWORD GUARD] Current state:', { 
      requiresChange: user?.requires_password_change,
      path: location.pathname,
      user: user ? { 
        email: user.email, 
        role: user.role,
        requires_password_change: user.requires_password_change
      } : null
    });
  }, [user, location.pathname]);

  // Never redirect if we're already on the change password page
  if (location.pathname === '/change-password') {
    return children;
  }

  // If still loading auth state, show nothing
  if (loading) {
    console.debug('[PASSWORD GUARD] Still loading auth state');
    return null;
  }

  // If user requires password change, redirect to change password page
  if (user && user.requires_password_change) {
    console.debug('[PASSWORD GUARD] User requires password change, redirecting from', location.pathname);
    return <Navigate to="/change-password" replace state={{ from: location }} />;
  }

  // Otherwise, render the children
  console.debug('[PASSWORD GUARD] Password OK, allowing access to', location.pathname);
  return children;
};

export default PasswordChangeGuard;
